import { LangProvider } from '@vextura/i18n';
import {
  AppShell,
  AuthProvider,
  ConsoleRouter,
  createMockAuthAdapter,
  ScopeProvider,
  UpsellProvider,
  useAuth,
  type AuthAdapter,
  type AuthUser,
} from '@vextura/shell';
import { applyVexturaTheme } from '@vextura/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { createDevBackedAuthAdapter, httpAuthAdapter, loadDevUser } from './auth-adapter.js';
import { getConsoleConfig } from './loadConfig.js';
import { MarketingLanding } from './landing/MarketingLanding.js';
import { LoginScreen } from './LoginScreen.js';
import { offlineScopeAdapter } from './scope-adapter.js';
import { SdkBindings } from './sdk-providers.js';
import { SECTIONS } from './sections.js';
import { SignUpScreen } from './SignUpScreen.js';
import { SignUpThanks } from './SignUpThanks.js';
import { SsoCallback } from './SsoCallback.js';

applyVexturaTheme();

const config = getConsoleConfig();

export function App() {
  const [authAdapter, setAuthAdapter] = useState<AuthAdapter | null>(
    config.mockAuth ? null : httpAuthAdapter,
  );

  useEffect(() => {
    if (!config.mockAuth) return;
    void loadDevUser().then((user: AuthUser) => {
      // Prefer the dev-backed adapter that mints REAL JWTs via vex-auth so
      // downstream SDK calls carry a token the gate actually accepts. Falls
      // back to the inert mock adapter (literal "mock-jwt-token") only if
      // dev-user.json is missing the seeded credentials — that path is
      // shell-only smoke and produces 401s on every API call. See
      // auth-adapter.ts::createDevBackedAuthAdapter.
      const clientId = user.id || 'admin';
      const clientSecret =
        (user as AuthUser & { devSecret?: string }).devSecret ||
        (import.meta.env['VITE_VEX_DEV_ADMIN_SECRET'] as string | undefined) ||
        '';
      const adapter = clientSecret
        ? createDevBackedAuthAdapter(user, clientId, clientSecret)
        : createMockAuthAdapter(user);
      setAuthAdapter(adapter);
    });
  }, []);

  if (!authAdapter) return null;

  return (
    <LangProvider>
      <BrowserRouter>
        <AuthProvider adapter={authAdapter}>
          <AuthScopedShell />
        </AuthProvider>
      </BrowserRouter>
    </LangProvider>
  );
}

/**
 * Router shell. Three URL surfaces:
 *   /login        — LoginScreen (SSO + username/password)
 *   /sso/callback — OIDC Authorization Code exchange landing page
 *   /*            — the authenticated app (shell + sections). Unauth users
 *                   are auto-redirected here to /login?reason=... with
 *                   the current path remembered so post-login we return them.
 *
 * When AuthProvider's expiry watchdog clears the token mid-session, the
 * <RequireAuth> wrapper re-renders and issues the redirect — that's the
 * "no error thrown, you just land on login" behaviour the user asked for.
 */
function AuthScopedShell(): ReactNode {
  const { loading, user } = useAuth();
  if (loading) return null;

  return (
    <Routes>
      <Route path="/signin" element={<LoginScreen />} />
      <Route path="/signup" element={<SignUpScreen />} />
      <Route path="/signup/thanks" element={<SignUpThanks />} />
      <Route path="/sso/callback" element={<SsoCallback />} />
      {/*
        Root path is auth-aware: signed-out visitors see the marketing
        landing, signed-in users get the console shell. Deep-links to
        service routes (/iam/users etc.) still fall through to RequireAuth
        below so anonymous visitors get bounced to /signin?return_to=...
      */}
      <Route path="/" element={user ? <TenantScopedShell /> : <MarketingLanding />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <TenantScopedShell />
          </RequireAuth>
        }
      />
    </Routes>
  );
}

function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    const q = new URLSearchParams({ reason: 'session_expired' });
    // Remember where the user was so post-login we could route them back
    // (kept for future; LoginScreen ignores it today).
    if (location.pathname && location.pathname !== '/') {
      q.set('return_to', location.pathname + location.search);
    }
    return <Navigate to={`/signin?${q.toString()}`} replace />;
  }
  return <>{children}</>;
}

/**
 * ScopeProvider + AppShell wired for a signed-in user. Tenant is locked to
 * the user's home tenant; cross-tenant access happens exclusively through
 * role assumption, never a tenant picker.
 *
 * Multi-region hardening (P1): the region seed prefers user.homeRegion
 * (extracted from the JWT `region` claim), falling back to the app's
 * static config.defaultRegion only when the token is pre-P1 legacy and
 * carries no region claim. Without this, a customer who logs in from
 * region A lands on an empty region until they pick from the dropdown —
 * every SDK call in that window either fails P8's cross-region enforcement
 * or hits a wrong-region gate.
 */
function TenantScopedShell(): ReactNode {
  const { user } = useAuth();
  const lockedTenant = user!.homeTenant || config.defaultTenant;
  const seedRegion = user!.homeRegion || config.defaultRegion;

  return (
    <ScopeProvider
      adapter={offlineScopeAdapter}
      {...(lockedTenant ? { lockedTenant } : {})}
      defaultRegion={seedRegion}
    >
      <SdkBindings>
        <UpsellProvider>
          <AppShell>
            <ConsoleRouter sections={SECTIONS} />
          </AppShell>
        </UpsellProvider>
      </SdkBindings>
    </ScopeProvider>
  );
}
